import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/AdminTestimonials.css";

type ApplicationStatus = "new" | "reviewed" | "shortlisted" | "rejected";
type ModalMode = "view" | "edit";

interface CareerApplicationRow {
  id: number;
  job_id: number;
  job_title: string;
  full_name: string;
  email: string;
  phone: string;
  cover_letter: string;
  cv_file: string;
  status: ApplicationStatus;
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
const FILE_BASE_URL =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000/uploads";

export default function AdminCareerApplications() {
  const { showModal } = useMessageModal();

  const [data, setData] = useState<CareerApplicationRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<CareerApplicationRow | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("view");
  const [activeRow, setActiveRow] = useState<any>(null);

  const fields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "job_title", label: "Job Title", type: "text", colSpan: 2 },
      { key: "full_name", label: "Full Name", type: "text", colSpan: 2 },
      { key: "email", label: "Email", type: "text", colSpan: 2 },
      { key: "phone", label: "Phone", type: "text", colSpan: 2 },
      {
        key: "cover_letter",
        label: "Cover Letter",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "New", value: "new" },
          { label: "Reviewed", value: "reviewed" },
          { label: "Shortlisted", value: "shortlisted" },
          { label: "Rejected", value: "rejected" },
        ],
        colSpan: 2,
      },
    ],
    [],
  );

  const getCvUrl = (cv?: string) => {
    if (!cv) return "";
    if (cv.startsWith("http://") || cv.startsWith("https://")) return cv;
    return `${FILE_BASE_URL}/uploads/cv/${cv}`;
  };

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "job_title", label: "Job Title" },
    { key: "full_name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={
            row.status === "shortlisted"
              ? "badge-success"
              : row.status === "rejected"
                ? "badge-danger"
                : "badge-warning"
          }
        >
          {row.status}
        </span>
      ),
    },
    {
      key: "cv_file",
      label: "CV",
      render: (row) =>
        row.cv_file ? (
          <a
            href={getCvUrl(row.cv_file)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-primary"
            onClick={(e) => e.stopPropagation()}
          >
            View CV
          </a>
        ) : (
          <span>-</span>
        ),
    },
    {
      key: "createdAt",
      label: "Applied On",
      render: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-",
    },
  ];

  const fetchApplications = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/career-applications`, {
        credentials: "include",
      });
      const result = await res.json();

      if (result.success) {
        const freshData = result.data || [];
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Applications Error:", error);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleSubmit = async (formData: any) => {
    try {
      const res = await fetch(
        `${BASE_URL}/admin/career-applications/${activeRow.id}/status`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: formData.status,
          }),
        },
      );

      const result = await res.json();

      if (result.success) {
        fetchApplications();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message: "Application status updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to update application.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Update Application Error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while updating application status.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Application",
      message: `Are you sure you want to delete application of "${row.full_name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        const res = await fetch(
          `${BASE_URL}/admin/career-applications/${row.id}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );

        const result = await res.json();

        if (result.success) {
          fetchApplications();
          setSelectedRow(null);

          showModal({
            type: "success",
            title: "Deleted",
            message: "Application deleted successfully.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Career Applications"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="career-applications"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Career Application"
        fields={fields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
